<template>
  <div class="modal-review">
    <NavBar>
      <template #left>
        <BackButton @click="$emit('close')" />
      </template>
    </NavBar>
    <main class="main">
      <HeadingDisplay
        class="main-title"
        title="Revise os dados do investimento"
        size="medium"
      />
      <ContentDisplay
        v-for="(portfolioReviewInfo, index) in props.portfolioReviewData
          .summary"
        :key="index"
        :blocks-length="1"
        class="content-container"
        :footer-label="portfolioReviewInfo.caption"
        footer-type="caption"
      >
        <template #content-block-1>
          <HeadingDisplay
            class="display-title"
            :title="portfolioReviewInfo.title"
            size="extra-small"
          />
          <div
            v-for="(
              portfolioReviewInfoContent, innerIndex
            ) in portfolioReviewInfo.rows"
            :key="innerIndex"
          >
            <Heading
              v-if="portfolioReviewInfoContent.title"
              :level="5"
              size="extra-small"
              class="inner-title"
            >
              {{ portfolioReviewInfoContent.title }}
            </Heading>
            <ListDisplay
              v-else
              :key="innerIndex"
              class="list-item"
              :title="portfolioReviewInfoContent.label"
              :value="portfolioReviewInfoContent.value"
              :show-icon="!!portfolioReviewInfoContent.content"
              icon="EF0080"
              @click="toggleDetailModal(portfolioReviewInfoContent)"
            />
          </div>
        </template>
      </ContentDisplay>
    </main>
    <footer class="footer">
      <ButtonContainer>
        <template #button-primary>
          <ButtonPrimary
            label="Continuar"
            size="large"
            :loading="loading"
            @click="$emit('submit')"
          />
        </template>
      </ButtonContainer>
    </footer>
    <DynamicContentModal
      :is-open="isDetailModalOpen"
      :title="modalTitle"
      :modal-info="modalInfo"
      :costs-button="showCostsButton"
      @close="toggleDetailModal"
    />
  </div>
</template>
<script setup lang="ts">
import {
  NavBar,
  ContentDisplay,
  ListDisplay,
  HeadingDisplay,
  ButtonContainer,
  ButtonPrimary,
  Heading
} from '@warrenbrasil/nebraska-web';
import { ref } from 'vue';
import { BackButton } from '@/modules/common/components/buttons/BackButton';
import {
  PortfolioReviewData,
  PortfolioReviewRow
} from '@/modules/wealth/services/portfolio';
import { IDynamicContent } from '@/modules/common/types';
import { DynamicContentModal } from '@/modules/common/components/DynamicContentModal';

interface Props {
  portfolioReviewData: PortfolioReviewData;
  loading: boolean;
}

const props = defineProps<Props>();

interface EmitEvents {
  (event: 'close'): void;
  (event: 'submit'): void;
}

defineEmits<EmitEvents>();

const modalTitle = ref('');
const modalInfo = ref<IDynamicContent[]>([]);
const showCostsButton = ref(false);
const isDetailModalOpen = ref(false);

const toggleDetailModal = (row: PortfolioReviewRow) => {
  if (row) {
    modalTitle.value = row.label;
    modalInfo.value = row.content || [];
    showCostsButton.value = !!row.action;
  }
  isDetailModalOpen.value = !isDetailModalOpen.value;
};
</script>

<style lang="less" scoped>
.modal-review {
  position: absolute;
  height: 100vh;
  width: 100%;
  top: 0;
  left: 0;
  background-color: @background-secondary;
  z-index: 11;
  @media screen and (max-width: 1280px) {
    height: 130vh;
  }

  .main-title {
    max-width: 330px;
    text-align: center;
  }

  .main {
    margin: 0 auto;
    padding: @size-spacing-x800 @size-spacing-x400;
    padding-bottom: @size-spacing-x2000;

    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    gap: @size-spacing-x700;

    .content-container {
      width: 488px;
      margin: 0 auto;
    }

    .inner-title {
      font-size: 19px;
      font-weight: 700;
      margin-bottom: @size-spacing-x400;
      margin-top: @size-spacing-x500;
    }

    .display-title {
      margin: 0 auto;
      margin-bottom: @size-spacing-x500;
    }

    .list-item {
      margin-bottom: @size-spacing-x150;
    }
  }

  .footer {
    position: fixed;
    bottom: 0;
    width: 100%;
    background-color: @background-secondary;
    z-index: 1;
  }
}
</style>
